import React, { useEffect, useState, useCallback } from 'react';
// Import API service functions
import {
    getAllMissions,
    findUserByPhone,
    getUserMissionProgress,
    updateUserMissionProgress,
    adminAddManualTransactionAndPoints,
    refreshOlseraData,
    // Asumsi ada fungsi baru ini di apiService.js
    submitIncomingVoucher,
} from '../../services/apiService'; // Sesuaikan path jika perlu
// Impor CSS
import './UserProgressDashboard.css'; // Pastikan path ini benar

// --- Helper Components (Tidak ada perubahan) ---
const LoadingSpinner = ({ small = false }) => (
    <div className={`loading-spinner ${small ? 'small' : ''}`}></div>
);
const ProcessingIndicator = ({ message = "Processing..." }) => (
    <div className="processing-indicator"> <LoadingSpinner small /> {message} </div>
);
const ErrorMessage = ({ message }) => (
    <div className="error-message" role="alert"> <strong>Error: </strong><span>{message}</span> </div>
);
const SuccessMessage = ({ message }) => (
    <div className="success-message" role="alert"> <strong>Success: </strong><span>{message}</span> </div>
);

// --- Initial Form State for Manual Transaction ---
const initialManualTransactionData = {
    transaction_amount: '',
    transaction_description: '',
    points_type_override: '',
};

// --- Main Component ---
function UserProgressDashboard() {
    // --- START: Penambahan State untuk Voucher ---
    const [incomingVoucherData, setIncomingVoucherData] = useState({ voucher_code: '', transaction_code: '' });
    const [processingVoucher, setProcessingVoucher] = useState(false);
    const [voucherMessage, setVoucherMessage] = useState({ type: '', text: '' });
    // --- END: Penambahan State untuk Voucher ---

    // State untuk pencarian user utama
    const [searchPhoneNumber, setSearchPhoneNumber] = useState('');
    const [searchedUserData, setSearchedUserData] = useState(null);
    const [loadingUserSearch, setLoadingUserSearch] = useState(false);
    const [userSearchError, setUserSearchError] = useState('');

    // State untuk Tab
    const [activeTab, setActiveTab] = useState('missionProgress');

    // State untuk Tab "User Mission Progress"
    const [missions, setMissions] = useState([]);
    const [loadingMissions, setLoadingMissions] = useState(false);
    const [selectedMissionIdInTab, setSelectedMissionIdInTab] = useState('');
    const [selectedMissionDataInTab, setSelectedMissionDataInTab] = useState(null);
    const [userTaskStatusInTab, setUserTaskStatusInTab] = useState({});
    const [loadingProgressInTab, setLoadingProgressInTab] = useState(false);
    const [processingUpdateInTab, setProcessingUpdateInTab] = useState(false);

    // State untuk Tab "Tambah Transaksi Manual"
    const [manualTransactionData, setManualTransactionData] = useState(initialManualTransactionData);
    const [processingManualTransaction, setProcessingManualTransaction] = useState(false);

    // State umum untuk error/sukses di dalam tab
    const [tabError, setTabError] = useState('');
    const [tabSuccess, setTabSuccess] = useState('');

    // State untuk FAB Olsera
    const [olseraStatus, setOlseraStatus] = useState({ message: '', type: '', loading: false });
    const [showOlseraNotification, setShowOlseraNotification] = useState(false);


    // --- START: Penambahan Handler untuk Voucher ---
    const handleIncomingVoucherChange = (e) => {
        const { name, value } = e.target;
        setIncomingVoucherData(prev => ({ ...prev, [name]: value }));
    };

    const handleIncomingVoucherSubmit = async (e) => {
        e.preventDefault();
        // Sesuaikan pengecekan
        if (!incomingVoucherData.voucher_code || !incomingVoucherData.transaction_code) {
            setVoucherMessage({ type: 'error', text: 'Semua field wajib diisi.' });
            return;
        }
        setProcessingVoucher(true);
        setVoucherMessage({ type: '', text: '' });

        try {
            // Sesuaikan payload
            const payload = {
                voucher_code: incomingVoucherData.voucher_code,
                transaction_code: incomingVoucherData.transaction_code
            };
            const response = await submitIncomingVoucher(payload);
            setVoucherMessage({ type: 'success', text: response.message || 'Voucher berhasil disimpan.' });
            // Sesuaikan reset form
            setIncomingVoucherData({ voucher_code: '', transaction_code: '' });
        } catch (err) {
            const message = err.errors ? Object.values(err.errors).flat().join(' ') : (err.message || 'Gagal menyimpan voucher.');
            setVoucherMessage({ type: 'error', text: message });
            console.error("Incoming voucher submit error:", err);
        } finally {
            setProcessingVoucher(false);
        }
    };
    // --- END: Penambahan Handler untuk Voucher ---

    // 1. Fetch daftar misi
    const fetchMissionsList = useCallback(async () => {
        setLoadingMissions(true);
        try {
            const response = await getAllMissions();
            setMissions(response.data || []);
        } catch (err) {
            console.error("Fetch missions list error:", err);
            if (activeTab === 'missionProgress') {
                setTabError('Gagal memuat daftar misi.');
            }
        } finally {
            setLoadingMissions(false);
        }
    }, [activeTab]);

    useEffect(() => {
        fetchMissionsList();
    }, [fetchMissionsList]);

    // (Sisa kode handler lainnya tidak berubah...)
    // 2. Handler untuk pencarian user utama
    const handleUserSearch = async (e) => {
        if (e) e.preventDefault();
        if (!searchPhoneNumber.trim()) {
            setUserSearchError('Nomor telepon harus diisi.');
            return;
        }
        setLoadingUserSearch(true);
        setUserSearchError('');
        setSearchedUserData(null);
        setActiveTab('missionProgress');
        setSelectedMissionIdInTab('');
        setSelectedMissionDataInTab(null);
        setUserTaskStatusInTab({});
        setManualTransactionData(initialManualTransactionData);
        setTabError('');
        setTabSuccess('');

        try {
            const response = await findUserByPhone(searchPhoneNumber.trim());
            if (response && response.data && response.data.id) {
                setSearchedUserData(response.data);
            } else {
                throw new Error(response?.message || 'Pengguna tidak ditemukan atau format API tidak sesuai.');
            }
        } catch (err) {
            const message = err.errors ? Object.values(err.errors).flat().join(' ') : (err.message || 'Gagal mencari pengguna.');
            setUserSearchError(message);
            console.error("User search error:", err);
            setSearchedUserData(null);
        } finally {
            setLoadingUserSearch(false);
        }
    };

    // 3. Handler saat misi dipilih di dalam tab "User Mission Progress"
    const handleMissionSelectInTab = async (missionId) => {
        setSelectedMissionIdInTab(missionId);
        setSelectedMissionDataInTab(null);
        setUserTaskStatusInTab({});
        setTabError(''); setTabSuccess('');

        if (!missionId || !searchedUserData) {
            return;
        }

        setLoadingProgressInTab(true);
        try {
            const response = await getUserMissionProgress(missionId, searchedUserData.user_phone);
            if (response && response.data) {
                const { mission, progress } = response.data;
                if (!mission) throw new Error("Data misi tidak ditemukan dalam respons API progres.");
                setSelectedMissionDataInTab(mission);
                let initialStatus = {};
                if (progress && Array.isArray(progress.task_status)) {
                    progress.task_status.forEach((status, index) => { initialStatus[index] = status ? 1 : 0; });
                }
                setUserTaskStatusInTab(initialStatus);
            } else {
                throw new Error('Format respons API progres misi tidak sesuai.');
            }
        } catch (err) {
            const message = err.errors ? Object.values(err.errors).flat().join(' ') : (err.message || 'Gagal memuat progres misi pengguna.');
            setTabError(message);
            console.error("Fetch user mission progress in tab error:", err);
            setSelectedMissionDataInTab(null);
        } finally {
            setLoadingProgressInTab(false);
        }
    };

    // 4. Handler untuk ubah status task di tab
    const handleUserTaskStatusChangeInTab = (taskIndex) => {
        setUserTaskStatusInTab(prevStatus => ({
            ...prevStatus, [taskIndex]: prevStatus[taskIndex] === 1 ? 0 : 1
        }));
    };

    // 5. Handler untuk update progress di tab
    const handleUpdateUserProgressInTab = async (e) => {
        e.preventDefault();
        if (!searchedUserData || !selectedMissionDataInTab) return;
        setProcessingUpdateInTab(true); setTabError(''); setTabSuccess('');
        const taskStatusArray = selectedMissionDataInTab.tasks?.map((_, index) => userTaskStatusInTab[index] || 0) || [];
        if (selectedMissionDataInTab.tasks && taskStatusArray.length !== selectedMissionDataInTab.tasks.length) {
            setTabError('Jumlah status tugas tidak sesuai.'); setProcessingUpdateInTab(false); return;
        }
        const updateData = { user_id: searchedUserData.id, mission_id: selectedMissionDataInTab.id, task_status: taskStatusArray };
        try {
            const response = await updateUserMissionProgress(updateData);
            if (response && (response.success || typeof response.message === 'string')) {
                setTabSuccess(response.message || 'Progres berhasil diperbarui.');
                handleUserSearch();
            } else { throw new Error(response?.message || 'Gagal memperbarui progres.'); }
        } catch (err) {
            const message = err.errors ? Object.values(err.errors).flat().join(' ') : (err.message || 'Error update progres.');
            setTabError(message); console.error("Update user progress error:", err);
        } finally { setProcessingUpdateInTab(false); }
    };

    // 6. Handler untuk input form transaksi manual
    const handleManualTransactionInputChange = (e) => {
        const { name, value } = e.target;
        setManualTransactionData(prev => ({ ...prev, [name]: value }));
    };

    // 7. Handler untuk submit transaksi manual
    const handleAddManualTransaction = async (e) => {
        e.preventDefault();
        if (!searchedUserData) {
            setTabError("Silakan cari pengguna terlebih dahulu."); return;
        }
        const amount = parseFloat(manualTransactionData.transaction_amount);
        if (isNaN(amount) || amount < 0) {
            setTabError("Jumlah transaksi harus angka numerik dan tidak boleh negatif.");
            return;
        }

        setProcessingManualTransaction(true); setTabError(''); setTabSuccess('');
        const transactionPayload = {
            user_id: searchedUserData.id,
            transaction_amount: amount,
            transaction_description: manualTransactionData.transaction_description.trim() || null,
            // Kirim nilai dari dropdown. Jika kosong, backend akan menggunakan default.
            points_type_override: manualTransactionData.points_type_override || null,
        };
        try {
            const response = await adminAddManualTransactionAndPoints(transactionPayload);
            if (response && response.message) {
                setTabSuccess(response.message);
                setManualTransactionData(initialManualTransactionData);
                handleUserSearch();
            } else {
                throw new Error(response?.message || 'Gagal menambahkan transaksi poin manual (respons API tidak jelas).');
            }
        } catch (err) {
            const message = err.errors ? Object.values(err.errors).flat().join(' ') : (err.message || 'Terjadi kesalahan saat menambah transaksi poin.');
            setTabError(message); console.error("Add manual transaction error:", err);
        } finally { setProcessingManualTransaction(false); }
    };

    // Handler FAB Olsera
    const handleRefreshOlsera = async () => {
        setOlseraStatus({ message: '', type: '', loading: true });
        setShowOlseraNotification(false);
        try {
            const response = await refreshOlseraData();
            if (response && (response.success || response.message)) {
                setOlseraStatus({ message: response.message || 'Refresh Olsera berhasil.', type: 'success', loading: false });
            } else { throw new Error(response?.message || 'Gagal refresh Olsera.'); }
        } catch (err) {
            setOlseraStatus({ message: err.message || 'Error refresh Olsera.', type: 'error', loading: false });
            console.error("Olsera refresh error:", err);
        } finally {
            setShowOlseraNotification(true);
            setTimeout(() => setShowOlseraNotification(false), 5000);
        }
    };


    const hasValidTasksInTab = selectedMissionDataInTab &&
        Array.isArray(selectedMissionDataInTab.tasks) &&
        Array.isArray(selectedMissionDataInTab.points_per_task) &&
        selectedMissionDataInTab.tasks.length === selectedMissionDataInTab.points_per_task.length &&
        selectedMissionDataInTab.tasks.length > 0;

    return (
        <div className="user-progress-page dashboard-layout">

            {/* --- START: Penambahan JSX untuk Voucher --- */}
            <div className="voucher-input-section card">
                <h2>Input Voucher Eksternal</h2>
                {voucherMessage.text && (
                    voucherMessage.type === 'success' ?
                        <SuccessMessage message={voucherMessage.text} /> :
                        <ErrorMessage message={voucherMessage.text} />
                )}
                <form onSubmit={handleIncomingVoucherSubmit} className="incoming-voucher-form">
                    <div className="form-group">
                        <label htmlFor="voucher_code">Kode Voucher</label>
                        <input
                            type="text"
                            id="voucher_code"
                            name="voucher_code"
                            value={incomingVoucherData.voucher_code}
                            onChange={handleIncomingVoucherChange}
                            placeholder="Masukkan kode voucher..."
                            disabled={processingVoucher}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="transaction_code">Kode Transaksi</label>
                        <input
                            type="text"
                            id="transaction_code"
                            name="transaction_code"
                            value={incomingVoucherData.transaction_code}
                            onChange={handleIncomingVoucherChange}
                            placeholder="Masukkan kode transaksi unik..."
                            disabled={processingVoucher}
                            required
                        />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={processingVoucher}>
                        {processingVoucher ? <ProcessingIndicator message="Menyimpan..." /> : 'Simpan Voucher'}
                    </button>
                </form>
            </div>
            {/* --- END: Penambahan JSX untuk Voucher --- */}


            <h2>Dasbor Progres Pengguna</h2>

            {userSearchError && <ErrorMessage message={userSearchError} />}
            {showOlseraNotification && olseraStatus.message && (
                <div className={`notification-toast ${olseraStatus.type}`}>
                    {olseraStatus.message}
                    <button onClick={() => setShowOlseraNotification(false)} className="toast-close-btn">&times;</button>
                </div>
            )}

            <div className="user-search-section card">
                <h3>Cari Pengguna</h3>
                <form onSubmit={handleUserSearch} className="find-user-form">
                    <div className="form-group">
                        <label htmlFor="search_phone_number">Nomor Telepon Pengguna</label>
                        <input type="text" id="search_phone_number" value={searchPhoneNumber} onChange={(e) => setSearchPhoneNumber(e.target.value)} placeholder="Masukkan nomor telepon..." disabled={loadingUserSearch} required />
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={loadingUserSearch || !searchPhoneNumber.trim()}>
                        {loadingUserSearch ? <ProcessingIndicator message="Mencari..." /> : 'Cari Pengguna'}
                    </button>
                </form>
            </div>
            {/* (Sisa JSX lainnya tidak berubah...) */}
            {searchedUserData && (
                <div className="user-details-and-tabs-section card">
                    <h3>Profil: {searchedUserData.user_name} (ID: {searchedUserData.id})</h3>
                    <div className="user-points-info">
                        <p><strong>No. Telepon:</strong> {searchedUserData.user_phone}</p>
                        <p><strong>Email:</strong> {searchedUserData.user_email || '-'}</p>
                        <p><strong>Total Poin:</strong> {searchedUserData.total_points ?? 0}</p>
                        <p><strong>Bergabung:</strong> {new Date(searchedUserData.created_at).toLocaleDateString('id-ID', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>

                    <div className="tabs-navigation">
                        <button className={`tab-button ${activeTab === 'missionProgress' ? 'active' : ''}`} onClick={() => setActiveTab('missionProgress')}>Progres Misi</button>
                        <button className={`tab-button ${activeTab === 'addTransaction' ? 'active' : ''}`} onClick={() => setActiveTab('addTransaction')}>Tambah Transaksi Manual</button>
                        <button className={`tab-button ${activeTab === 'addRedeem' ? 'active' : ''}`} onClick={() => setActiveTab('addRedeem')}>Tambah Redeem Manual</button>
                    </div>

                    <div className="tab-content">
                        {tabError && <ErrorMessage message={tabError} />}
                        {tabSuccess && <SuccessMessage message={tabSuccess} />}

                        {activeTab === 'missionProgress' && (
                            <div className="mission-progress-tab">
                                <div className="form-group">
                                    <h4>Progress Misi</h4>
                                    <label htmlFor="mission_id_tab">Pilih Misi untuk Progres</label>
                                    <select id="mission_id_tab" value={selectedMissionIdInTab} onChange={(e) => handleMissionSelectInTab(e.target.value)} disabled={loadingMissions || loadingProgressInTab} >
                                        <option value="">-- Pilih Misi --</option>
                                        {missions.map(m => (<option key={m.id} value={m.id}>{m.mission_title} (ID: {m.id})</option>))}
                                    </select>
                                    {loadingMissions && <span className="loading-inline"><LoadingSpinner small /> Memuat daftar misi...</span>}
                                </div>
                                {loadingProgressInTab && <div className="loading-container tab-loading"><LoadingSpinner /> Memuat progres...</div>}
                                {selectedMissionDataInTab && !loadingProgressInTab && (
                                    <form onSubmit={handleUpdateUserProgressInTab}>
                                        <h4>Detail Misi: {selectedMissionDataInTab.title}</h4>
                                        <div className="form-group">
                                            <label>Status Tugas</label>
                                            <div className="task-status-list">
                                                {hasValidTasksInTab ? (
                                                    selectedMissionDataInTab.tasks.map((title, index) => (
                                                        <div key={index} className="task-status-item">
                                                            <input type="checkbox" id={`user_task_status_tab_${index}`} checked={userTaskStatusInTab[index] === 1} onChange={() => handleUserTaskStatusChangeInTab(index)} disabled={processingUpdateInTab || userTaskStatusInTab[index] === 1} />
                                                            <label htmlFor={`user_task_status_tab_${index}`}>
                                                                {title || `Tugas #${index + 1}`}
                                                                <span>({selectedMissionDataInTab.points_per_task[index] ?? 0} Poin)</span>
                                                            </label>
                                                        </div>
                                                    ))
                                                ) : (<p className="no-tasks-message">Misi ini tidak memiliki tugas yang valid.</p>)}
                                            </div>
                                        </div>
                                        {hasValidTasksInTab && (
                                            <button type="submit" className="btn btn-success" disabled={processingUpdateInTab}>
                                                {processingUpdateInTab ? <ProcessingIndicator message="Memperbarui..." /> : 'Update Progres'}
                                            </button>
                                        )}
                                    </form>
                                )}
                                {!selectedMissionDataInTab && !loadingProgressInTab && selectedMissionIdInTab && (<p className="info-message">Memuat detail untuk misi yang dipilih...</p>)}
                                {!selectedMissionIdInTab && !loadingProgressInTab && (<p className="info-message">Pilih misi dari dropdown di atas.</p>)}
                            </div>
                        )}

                        {activeTab === 'addTransaction' && (
                            <div className="manual-transaction-tab">
                                <h4>Tambah Transaksi Manual</h4>
                                <form onSubmit={handleAddManualTransaction}>
                                    <div className="form-group">
                                        <label htmlFor="manual_transaction_amount">Jumlah Transaksi (Rp)</label>
                                        <input
                                            type="number"
                                            id="manual_transaction_amount"
                                            name="transaction_amount"
                                            value={manualTransactionData.transaction_amount}
                                            onChange={handleManualTransactionInputChange}
                                            placeholder="Contoh: 50000 (tanpa titik/koma)"
                                            min="0"
                                            required
                                            disabled={processingManualTransaction}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="manual_transaction_description" className="optional">Catatan Transaksi (Opsional)</label>
                                        <input
                                            type="text"
                                            id="manual_transaction_description"
                                            name="transaction_description"
                                            value={manualTransactionData.transaction_description}
                                            onChange={handleManualTransactionInputChange}
                                            placeholder="Catatan tambahan untuk transaksi ini"
                                            disabled={processingManualTransaction}
                                        />
                                    </div>
                                    {/* === PERUBAHAN INPUT MENJADI DROPDOWN === */}
                                    <div className="form-group">
                                        <label htmlFor="manual_points_type_override" className="optional">Tipe Poin</label>
                                        <select
                                            id="manual_points_type_override"
                                            name="points_type_override" // Sesuai state & API
                                            value={manualTransactionData.points_type_override}
                                            onChange={handleManualTransactionInputChange}
                                            disabled={processingManualTransaction}
                                        >
                                            <option value="">-- Pilih Tipe (Default: Manual Transaction Posting) --</option>
                                            <option value="transaksi">Transaksi</option>
                                            <option value="misi">Misi</option>
                                            {/* Tambahkan opsi lain jika perlu */}
                                        </select>
                                    </div>
                                    {/* === AKHIR PERUBAHAN === */}
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={processingManualTransaction}
                                    >
                                        {processingManualTransaction ? <ProcessingIndicator message="Memproses..." /> : 'Simpan Transaksi & Poin'}
                                    </button>
                                </form>
                            </div>
                        )}

                        {activeTab === 'addRedeem' && (
                            <div className="dummy-tab-content">
                                <p>Fitur "Tambah Redeem Manual" akan segera tersedia.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
            <button className={`fab ${olseraStatus.loading ? 'loading' : ''}`} onClick={handleRefreshOlsera} disabled={olseraStatus.loading} title="Refresh Data Transaksi Olsera">
                {olseraStatus.loading ? <LoadingSpinner small /> : (
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="fab-icon">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                )}
            </button>
        </div>
    );
}

export default UserProgressDashboard;